import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { 
  RefreshCw, 
  Play, 
  Pause, 
  Clock, 
  Calendar, 
  CheckCircle, 
  XCircle,
  Zap,
  Settings,
  TrendingUp,
  Package,
  ShoppingCart,
  ArrowUpRight,
  RotateCcw,
  XOctagon,
  Download
} from "lucide-react";

const API = "/api";

export default function AutoSyncPage() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  
  // Sync durumu
  const [syncStatus, setSyncStatus] = useState({
    enabled: false,
    sync_interval_minutes: 5,
    last_sync_time: null,
    last_sync_result: null,
    last_sync_products_count: 0,
    sync_stock: true,
    sync_price: true,
    job_running: false
  });
  
  // Manuel sync için tarih
  const [syncDate, setSyncDate] = useState(() => {
    const now = new Date();
    now.setHours(now.getHours() - 1);
    return now.toISOString().slice(0, 16);
  });
  
  // Ayarlar
  const [intervalMinutes, setIntervalMinutes] = useState("5");
  const [syncStock, setSyncStock] = useState(true);
  const [syncPrice, setSyncPrice] = useState(true);
  
  // Sipariş sync ayarları
  const [syncOrders, setSyncOrders] = useState(true);
  const [autoTransferOrders, setAutoTransferOrders] = useState(false);
  const [autoCancelSync, setAutoCancelSync] = useState(true);
  
  // Sync geçmişi
  const [syncHistory, setSyncHistory] = useState([]);
  
  // İşlem durumları
  const [orderSyncing, setOrderSyncing] = useState(false);
  const [transferring, setTransferring] = useState(false);
  const [cancelSyncing, setCancelSyncing] = useState(false);
  
  // Pazaryeri sync
  const [marketplaceSyncing, setMarketplaceSyncing] = useState(false);
  const [marketplaceSyncResult, setMarketplaceSyncResult] = useState(null);
  
  // Pazaryeri Scheduler
  const [mpSchedulerSettings, setMpSchedulerSettings] = useState({
    enabled: false,
    pull_interval_minutes: 15,
    auto_sync_to_shopify: false,
    marketplaces: []
  });
  const [mpSchedulerStatus, setMpSchedulerStatus] = useState(null);
  const [mpSchedulerLoading, setMpSchedulerLoading] = useState(false);

  useEffect(() => {
    fetchSyncStatus();
    fetchSyncHistory();
    fetchMpSchedulerSettings();
  }, [token]);

  const fetchMpSchedulerSettings = async () => {
    try {
      const [settingsRes, statusRes] = await Promise.all([
        axios.get(`${API}/marketplace-orders/scheduler/settings?token=${token}`),
        axios.get(`${API}/marketplace-orders/scheduler/status?token=${token}`)
      ]);
      setMpSchedulerSettings(settingsRes.data);
      setMpSchedulerStatus(statusRes.data);
    } catch (error) {
      console.error("Marketplace scheduler ayarları alınamadı:", error);
    }
  };

  const startMpScheduler = async () => {
    setMpSchedulerLoading(true);
    try {
      await axios.put(`${API}/marketplace-orders/scheduler/settings?token=${token}`, {
        ...mpSchedulerSettings,
        enabled: true
      });
      toast.success("Pazaryeri sipariş servisi başlatıldı");
      fetchMpSchedulerSettings();
    } catch (error) {
      toast.error("Servis başlatılamadı");
    } finally {
      setMpSchedulerLoading(false);
    }
  };

  const stopMpScheduler = async () => {
    setMpSchedulerLoading(true);
    try {
      await axios.put(`${API}/marketplace-orders/scheduler/settings?token=${token}`, {
        ...mpSchedulerSettings,
        enabled: false
      });
      toast.success("Pazaryeri sipariş servisi durduruldu");
      fetchMpSchedulerSettings();
    } catch (error) {
      toast.error("Servis durdurulamadı");
    } finally {
      setMpSchedulerLoading(false);
    }
  };

  const runMpSchedulerNow = async () => {
    setMarketplaceSyncing(true);
    try {
      await axios.post(`${API}/marketplace-orders/scheduler/run-now?token=${token}`);
      toast.success("Sipariş çekme ve aktarma tamamlandı");
      fetchMpSchedulerSettings();
      fetchSyncHistory();
    } catch (error) {
      toast.error("İşlem başarısız");
    } finally {
      setMarketplaceSyncing(false);
    }
  };

  const saveMpSchedulerSettings = async () => {
    setMpSchedulerLoading(true);
    try {
      await axios.put(`${API}/marketplace-orders/scheduler/settings?token=${token}`, mpSchedulerSettings);
      toast.success("Pazaryeri sync ayarları kaydedildi");
      fetchMpSchedulerSettings();
    } catch (error) {
      toast.error("Ayarlar kaydedilemedi");
    } finally {
      setMpSchedulerLoading(false);
    }
  };

  const fetchSyncStatus = async () => {
    try {
      const response = await axios.get(`${API}/auto-sync/status?token=${token}`);
      setSyncStatus(response.data);
      setIntervalMinutes(String(response.data.sync_interval_minutes || 5));
      setSyncStock(response.data.sync_stock !== false);
      setSyncPrice(response.data.sync_price !== false);
    } catch (error) {
      console.error("Sync durumu alınamadı:", error);
    }
  };

  const fetchSyncHistory = async () => {
    try {
      const response = await axios.get(`${API}/logs?token=${token}&limit=30`);
      setSyncHistory(response.data.logs || []);
    } catch (error) {
      console.error("Sync geçmişi alınamadı:", error);
    }
  };

  const startAutoSync = async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/auto-sync/start?token=${token}&interval_minutes=${intervalMinutes}`
      );
      toast.success(response.data.message);
      fetchSyncStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Sync başlatılamadı");
    } finally {
      setLoading(false);
    }
  };

  const stopAutoSync = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auto-sync/stop?token=${token}`);
      toast.success(response.data.message);
      fetchSyncStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Sync durdurulamadı");
    } finally {
      setLoading(false);
    }
  };

  const runSyncNow = async () => {
    setSyncing(true);
    try {
      const response = await axios.post(`${API}/auto-sync/run-now?token=${token}`);
      toast.success(response.data.message);
      setTimeout(() => {
        fetchSyncStatus();
        fetchSyncHistory();
      }, 3000);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Sync başlatılamadı");
    } finally {
      setSyncing(false);
    }
  };

  const runManualSync = async () => {
    setSyncing(true);
    try {
      const response = await axios.post(`${API}/nebim/sync-and-update?token=${token}`, {
        date: syncDate
      });
      
      if (response.data.success) {
        toast.success(`${response.data.updated_count || 0} ürün güncellendi`);
        fetchSyncHistory();
      } else {
        toast.error(response.data.error || "Sync başarısız");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Sync hatası");
    } finally {
      setSyncing(false);
    }
  };

  const updateSettings = async () => {
    setLoading(true);
    try {
      await axios.put(
        `${API}/auto-sync/settings?token=${token}&sync_stock=${syncStock}&sync_price=${syncPrice}&interval_minutes=${intervalMinutes}`
      );
      toast.success("Ayarlar kaydedildi");
      fetchSyncStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Ayarlar kaydedilemedi");
    } finally {
      setLoading(false);
    }
  };

  // ============ SİPARİŞ SERVİSLERİ ============
  
  const syncShopifyOrders = async () => {
    setOrderSyncing(true);
    try {
      const response = await axios.post(`${API}/shopify/sync-orders?token=${token}`);
      if (response.data.success) {
        toast.success(response.data.message);
        fetchSyncHistory();
      } else {
        toast.error(response.data.error || "Sipariş çekme başarısız");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Shopify sipariş hatası");
    } finally {
      setOrderSyncing(false);
    }
  };

  const transferPendingOrders = async () => {
    setTransferring(true);
    try {
      // Önce aktarılmamış siparişleri bul
      const ordersResponse = await axios.get(`${API}/orders?token=${token}&transferred=false&limit=100`);
      const pendingOrders = ordersResponse.data.orders || [];
      
      if (pendingOrders.length === 0) {
        toast.info("Aktarılacak sipariş bulunamadı");
        return;
      }
      
      const orderIds = pendingOrders.map(o => o.id);
      
      const response = await axios.post(`${API}/orders/transfer-to-nebim?token=${token}`, orderIds);
      
      if (response.data.success) {
        toast.success(`${response.data.transferred_count} sipariş Nebim'e aktarıldı`);
        fetchSyncHistory();
      } else {
        toast.error("Aktarma başarısız");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Sipariş aktarma hatası");
    } finally {
      setTransferring(false);
    }
  };

  const syncCancellations = async () => {
    setCancelSyncing(true);
    try {
      const response = await axios.post(`${API}/orders/sync-cancellations?token=${token}`);
      
      if (response.data.success) {
        toast.success(response.data.message);
        fetchSyncHistory();
      } else {
        toast.error("İptal senkronizasyonu başarısız");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "İptal sync hatası");
    } finally {
      setCancelSyncing(false);
    }
  };

  // Tüm pazaryerlerinden sipariş çek ve Shopify'a aktar
  const syncAllMarketplaces = async () => {
    setMarketplaceSyncing(true);
    setMarketplaceSyncResult(null);
    
    try {
      // Son 1 günlük siparişleri çek ve aktar
      const response = await axios.post(
        `${API}/marketplace-orders/auto-sync?token=${token}&marketplace=all&limit=50&pull_first=true`,
        {},
        { timeout: 600000 }  // 10 dakika timeout
      );
      
      setMarketplaceSyncResult(response.data);
      
      if (response.data.synced > 0) {
        toast.success(`✅ ${response.data.synced} sipariş Shopify'a aktarıldı`);
      }
      if (response.data.pulled > 0) {
        toast.info(`📦 ${response.data.pulled} yeni sipariş çekildi`);
      }
      if (response.data.errors?.length > 0) {
        toast.warning(`⚠️ ${response.data.errors.length} sipariş aktarılamadı`);
      }
      if (response.data.synced === 0 && response.data.pulled === 0 && !response.data.errors?.length) {
        toast.info("ℹ️ Son 1 günde aktarılacak yeni sipariş yok");
      }
      
      fetchSyncHistory();
    } catch (error) {
      console.error("Marketplace sync error:", error);
      let errorMsg = "Pazaryeri senkronizasyonu başarısız";
      
      if (error.code === 'ECONNABORTED') {
        errorMsg = "İşlem zaman aşımına uğradı. Arka planda devam ediyor olabilir.";
        toast.warning(errorMsg);
      } else if (error.response?.data?.detail) {
        errorMsg = error.response.data.detail;
        toast.error(errorMsg);
      } else {
        toast.error(errorMsg);
      }
      
      setMarketplaceSyncResult({ success: false, error: errorMsg });
    } finally {
      setMarketplaceSyncing(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleString("tr-TR");
  };

  return (
    <div className="space-y-6 fade-in" data-testid="auto-sync-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Otomatik Senkronizasyon</h1>
          <p className="text-slate-500 mt-1">Ürün, sipariş ve stok senkronizasyonlarını yönetin</p>
        </div>
        <Badge 
          className={syncStatus.enabled ? "bg-green-500" : "bg-slate-400"}
          data-testid="sync-status-badge"
        >
          {syncStatus.enabled ? "AKTİF" : "DEVRE DIŞI"}
        </Badge>
      </div>

      {/* SİPARİŞ SERVİSLERİ */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Shopify'dan Sipariş Çek */}
        <Card className="border-blue-200 bg-blue-50/30">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-blue-700">
              <Download className="w-5 h-5" />
              Sipariş Çek
            </CardTitle>
            <CardDescription>
              Shopify'dan siparişleri çek
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button 
              onClick={syncShopifyOrders}
              disabled={orderSyncing}
              className="w-full bg-blue-600 hover:bg-blue-700"
              data-testid="btn-sync-orders"
            >
              {orderSyncing ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <ShoppingCart className="w-4 h-4 mr-2" />
              )}
              {orderSyncing ? "Çekiliyor..." : "Shopify'dan Siparişleri Çek"}
            </Button>
            <p className="text-xs text-slate-500 mt-2">
              Tüm siparişler (iptal ve iade dahil) çekilir
            </p>
          </CardContent>
        </Card>

        {/* Siparişleri Nebim'e Aktar */}
        <Card className="border-emerald-200 bg-emerald-50/30">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-emerald-700">
              <ArrowUpRight className="w-5 h-5" />
              Sipariş Aktar
            </CardTitle>
            <CardDescription>
              Bekleyen siparişleri Nebim'e aktar
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button 
              onClick={transferPendingOrders}
              disabled={transferring}
              className="w-full bg-emerald-600 hover:bg-emerald-700"
              data-testid="btn-transfer-orders"
            >
              {transferring ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <ArrowUpRight className="w-4 h-4 mr-2" />
              )}
              {transferring ? "Aktarılıyor..." : "Siparişleri Nebim'e Aktar"}
            </Button>
            <p className="text-xs text-slate-500 mt-2">
              Henüz aktarılmamış tüm siparişler gönderilir
            </p>
          </CardContent>
        </Card>

        {/* İptal/İade Senkronize Et */}
        <Card className="border-red-200 bg-red-50/30">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-700">
              <XOctagon className="w-5 h-5" />
              İptal/İade Sync
            </CardTitle>
            <CardDescription>
              İptal ve iadeleri Nebim'e bildir
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button 
              onClick={syncCancellations}
              disabled={cancelSyncing}
              className="w-full bg-red-600 hover:bg-red-700"
              data-testid="btn-sync-cancels"
            >
              {cancelSyncing ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <RotateCcw className="w-4 h-4 mr-2" />
              )}
              {cancelSyncing ? "Senkronize Ediliyor..." : "İptal/İadeleri Senkronize Et"}
            </Button>
            <p className="text-xs text-slate-500 mt-2">
              Shopify'da iptal/iade edilenler Nebim'e bildirilir
            </p>
          </CardContent>
        </Card>
      </div>

      {/* PAZARYERİ SERVİSLERİ */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pazaryeri Siparişleri - Otomatik Servis */}
        <Card className="border-purple-200 bg-purple-50/30">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-purple-700">
              <ShoppingCart className="w-5 h-5" />
              Pazaryeri Sipariş Sync Durumu
            </CardTitle>
            <CardDescription>
              Periyodik sipariş çekme ve Shopify'a aktarma
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-xs text-slate-500">Son Çalışma</p>
                <p className="font-medium text-sm">
                  {formatDate(mpSchedulerStatus?.last_pull_time)}
                </p>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-xs text-slate-500">Son Sonuç</p>
                <p className="font-medium text-sm">
                  {mpSchedulerStatus?.settings?.last_result || "-"}
                </p>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-xs text-slate-500">Çekilen</p>
                <p className="font-medium text-sm">
                  {mpSchedulerStatus?.settings?.last_pull_count || 0} sipariş
                </p>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-xs text-slate-500">Çalışma Aralığı</p>
                <p className="font-medium text-sm">
                  Her {mpSchedulerSettings?.pull_interval_minutes || 15} dakika
                </p>
              </div>
            </div>

            <div className="flex gap-2">
              {mpSchedulerSettings?.enabled ? (
                <Button 
                  onClick={stopMpScheduler}
                  disabled={mpSchedulerLoading}
                  variant="destructive"
                  className="flex-1"
                  data-testid="btn-stop-mp-scheduler"
                >
                  <Pause className="w-4 h-4 mr-2" />
                  Durdur
                </Button>
              ) : (
                <Button 
                  onClick={startMpScheduler}
                  disabled={mpSchedulerLoading}
                  className="flex-1 bg-purple-600 hover:bg-purple-700"
                  data-testid="btn-start-mp-scheduler"
                >
                  <Play className="w-4 h-4 mr-2" />
                  Başlat
                </Button>
              )}
              <Button 
                onClick={runMpSchedulerNow}
                disabled={mpSchedulerLoading || marketplaceSyncing}
                variant="outline"
                data-testid="btn-run-mp-now"
              >
                {marketplaceSyncing ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4" />
                )}
              </Button>
            </div>
            
            <p className="text-xs text-slate-500">
              Trendyol, N11, Çiçeksepeti'nden otomatik sipariş çekme ve Shopify'a aktarma
            </p>
          </CardContent>
        </Card>
        
        {/* Pazaryeri Scheduler Ayarları */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-purple-500" />
              Pazaryeri Sync Ayarları
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Çalışma Aralığı</Label>
              <Select 
                value={String(mpSchedulerSettings?.pull_interval_minutes || 15)} 
                onValueChange={(v) => setMpSchedulerSettings(prev => ({...prev, pull_interval_minutes: parseInt(v)}))}
              >
                <SelectTrigger data-testid="select-mp-interval">
                  <SelectValue placeholder="Aralık seçin" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="5">Her 5 dakika</SelectItem>
                  <SelectItem value="10">Her 10 dakika</SelectItem>
                  <SelectItem value="15">Her 15 dakika</SelectItem>
                  <SelectItem value="30">Her 30 dakika</SelectItem>
                  <SelectItem value="60">Her 1 saat</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Shopify'a Otomatik Aktar</Label>
                  <p className="text-xs text-slate-500">Çekilen siparişleri otomatik aktar</p>
                </div>
                <Switch 
                  checked={mpSchedulerSettings?.auto_sync_to_shopify || false} 
                  onCheckedChange={(checked) => setMpSchedulerSettings(prev => ({...prev, auto_sync_to_shopify: checked}))}
                  data-testid="switch-auto-shopify"
                />
              </div>
            </div>

            <Button 
              onClick={saveMpSchedulerSettings}
              disabled={mpSchedulerLoading}
              className="w-full"
              data-testid="btn-save-mp-settings"
            >
              {mpSchedulerLoading && <RefreshCw className="w-4 h-4 animate-spin mr-2" />}
              Ayarları Kaydet
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* ÜRÜN SERVİSLERİ */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Manuel Sync Kartı */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-blue-500" />
              Ürün Senkronizasyonu (Manuel)
            </CardTitle>
            <CardDescription>
              Belirli bir tarihten sonra değişen ürünleri çek ve güncelle
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="sync-date">Başlangıç Tarihi</Label>
              <Input
                id="sync-date"
                type="datetime-local"
                value={syncDate}
                onChange={(e) => setSyncDate(e.target.value)}
                className="w-full"
                data-testid="sync-date-input"
              />
              <p className="text-xs text-slate-500">
                Bu tarihten sonra değişen ürünler çekilecek
              </p>
            </div>
            
            <Button 
              onClick={runManualSync}
              disabled={syncing}
              className="w-full bg-blue-600 hover:bg-blue-700"
              data-testid="btn-manual-sync"
            >
              {syncing ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Zap className="w-4 h-4 mr-2" />
              )}
              {syncing ? "Çekiliyor..." : "Değişen Ürünleri Çek ve Güncelle"}
            </Button>
          </CardContent>
        </Card>

        {/* Otomatik Sync Durumu */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-emerald-500" />
              Otomatik Ürün Sync Durumu
            </CardTitle>
            <CardDescription>
              Periyodik stok/fiyat güncellemesi
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-xs text-slate-500">Son Çalışma</p>
                <p className="font-medium text-sm">
                  {formatDate(syncStatus.last_sync_time)}
                </p>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-xs text-slate-500">Son Sonuç</p>
                <p className="font-medium text-sm">
                  {syncStatus.last_sync_result || "-"}
                </p>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-xs text-slate-500">Kontrol Edilen</p>
                <p className="font-medium text-sm">
                  {syncStatus.last_sync_products_count || 0} ürün
                </p>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-xs text-slate-500">Çalışma Aralığı</p>
                <p className="font-medium text-sm">
                  Her {syncStatus.sync_interval_minutes} dakika
                </p>
              </div>
            </div>

            <div className="flex gap-2">
              {syncStatus.enabled ? (
                <Button 
                  onClick={stopAutoSync}
                  disabled={loading}
                  variant="destructive"
                  className="flex-1"
                  data-testid="btn-stop-sync"
                >
                  <Pause className="w-4 h-4 mr-2" />
                  Durdur
                </Button>
              ) : (
                <Button 
                  onClick={startAutoSync}
                  disabled={loading}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                  data-testid="btn-start-sync"
                >
                  <Play className="w-4 h-4 mr-2" />
                  Başlat
                </Button>
              )}
              <Button 
                onClick={runSyncNow}
                disabled={syncing}
                variant="outline"
                data-testid="btn-run-now"
              >
                {syncing ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4" />
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Sync Ayarları */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-slate-500" />
              Sync Ayarları
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Çalışma Aralığı</Label>
              <Select value={intervalMinutes} onValueChange={setIntervalMinutes}>
                <SelectTrigger data-testid="select-interval">
                  <SelectValue placeholder="Aralık seçin" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">Her 1 dakika</SelectItem>
                  <SelectItem value="2">Her 2 dakika</SelectItem>
                  <SelectItem value="5">Her 5 dakika</SelectItem>
                  <SelectItem value="10">Her 10 dakika</SelectItem>
                  <SelectItem value="15">Her 15 dakika</SelectItem>
                  <SelectItem value="30">Her 30 dakika</SelectItem>
                  <SelectItem value="60">Her 1 saat</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Stok Güncelle</Label>
                  <p className="text-xs text-slate-500">Stok miktarlarını senkronize et</p>
                </div>
                <Switch 
                  checked={syncStock} 
                  onCheckedChange={setSyncStock}
                  data-testid="switch-sync-stock"
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <Label>Fiyat Güncelle</Label>
                  <p className="text-xs text-slate-500">Satış fiyatlarını senkronize et</p>
                </div>
                <Switch 
                  checked={syncPrice} 
                  onCheckedChange={setSyncPrice}
                  data-testid="switch-sync-price"
                />
              </div>
            </div>

            <Button 
              onClick={updateSettings}
              disabled={loading}
              className="w-full"
              data-testid="btn-save-settings"
            >
              Ayarları Kaydet
            </Button>
          </CardContent>
        </Card>

        {/* Sync Geçmişi */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-purple-500" />
              Son İşlemler
            </CardTitle>
          </CardHeader>
          <CardContent>
            {syncHistory.length === 0 ? (
              <div className="text-center py-8">
                <Package className="w-12 h-12 mx-auto text-slate-300 mb-2" />
                <p className="text-slate-500 text-sm">Henüz işlem yapılmamış</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {syncHistory.map((log, idx) => (
                  <div 
                    key={idx} 
                    className="flex items-start gap-3 p-2 rounded hover:bg-slate-50"
                  >
                    {log.log_type === "success" ? (
                      <CheckCircle className="w-4 h-4 text-green-500 mt-1 flex-shrink-0" />
                    ) : log.log_type === "warning" ? (
                      <XCircle className="w-4 h-4 text-yellow-500 mt-1 flex-shrink-0" />
                    ) : (
                      <XCircle className="w-4 h-4 text-red-500 mt-1 flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{log.message}</p>
                      <p className="text-xs text-slate-500">
                        {formatDate(log.created_at)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
